import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { forgotPasswordSchema } from '@flymind/shared';
import { useTranslation } from '../hooks/useTranslation';
import { api } from '../lib/api-client';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';

export default function ForgotPassword() {
  const { t } = useTranslation();

  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setErrors({});

    const result = forgotPasswordSchema.safeParse({ email });
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of result.error.issues) {
        const field = issue.path[0] as string;
        if (!fieldErrors[field]) {
          fieldErrors[field] = issue.message;
        }
      }
      setErrors(fieldErrors);
      return;
    }

    setIsLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: result.data.email });
    } catch {
      // Always show success to prevent email enumeration
    } finally {
      setIsLoading(false);
      setIsSuccess(true);
    }
  }

  return (
    <div>
      <div className="mb-6 text-center">
        <h2 className="text-xl font-bold text-text-primary">
          {t('auth.resetPasswordTitle')}
        </h2>
        <p className="mt-1 text-sm text-text-secondary">
          {t('auth.resetPasswordSubtitle')}
        </p>
      </div>

      {isSuccess ? (
        <div className="flex flex-col gap-6">
          <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-4 text-center">
            <svg
              className="mx-auto mb-2 h-10 w-10 text-green-500"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
              />
            </svg>
            <p className="text-sm text-green-700">
              {t('auth.resetPasswordSent')}
            </p>
          </div>

          <Link to="/login">
            <Button variant="outline" size="lg" className="w-full">
              {t('auth.backToLogin')}
            </Button>
          </Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <Input
            label={t('auth.emailLabel')}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('auth.emailPlaceholder')}
            error={errors.email}
            required
            autoComplete="email"
          />

          <Button type="submit" size="lg" isLoading={isLoading} className="w-full">
            {t('auth.sendResetLink')}
          </Button>

          <p className="text-center text-sm text-text-secondary">
            <Link
              to="/login"
              className="font-medium text-primary-600 hover:text-primary-700 hover:underline"
            >
              {t('auth.backToLogin')}
            </Link>
          </p>
        </form>
      )}
    </div>
  );
}
